import { useCallback, useEffect, useState } from 'react';
import { broadcastRegistry } from 'lib/BroadcastChannelManager';

interface IUseCrossTabCountProps {
  channelName: string;
  storageKey: string;
}

interface IUseCrossTabCountReturn {
  count: number;
  increment: () => void;
  reset: () => void;
}

/**
 * Hook that syncs a numeric counter across browser tabs via the
 * broadcast registry, with localStorage persistence. The registry
 * ensures a single manager instance per channel name.
 *
 * @example
 * const { count, increment, reset } = useCrossTabCount({
 *   channelName: 'store-refresh',
 *   storageKey: 'store-refresh-count',
 * });
 */
export const useCrossTabCount = (
  props: IUseCrossTabCountProps,
): IUseCrossTabCountReturn => {
  const manager = broadcastRegistry.getCounter(props.channelName, props.storageKey);
  const [count, setCount] = useState<number>(() => manager.getCount());

  useEffect((): (() => void) => {
    const unsubscribe = manager.subscribe((newValue: number): void => {
      setCount(newValue);
    });

    return unsubscribe;
  }, [manager]);

  const increment = useCallback((): void => {
    const newCount = manager.increment();
    setCount(newCount);
  }, [manager]);

  const reset = useCallback((): void => {
    manager.reset();
    setCount(manager.getCount());
  }, [manager]);

  return { count, increment, reset };
};
